import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import {
  Plus,
  Search,
  Download,
  FileText,
  Eye,
  Edit,
  Package,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  creditDebitNoteApiService,
  CreditDebitNoteHeader,
  CreditDebitNoteListStatus,
  CreditDebitNoteSearchFilters,
  NoteType,
} from '../services/creditDebitNoteApi';
import { formatDate } from '../utils/dateUtils';
import { isDraftDocumentStatus } from '../utils/documentStatusUtils';
import { useBranch } from '../contexts/BranchContext';
import { getInvoiceConfig } from '../config/uiConfig';

const CREDIT_DEBIT_NOTE_LIST_PAGE_SIZE = getInvoiceConfig().listPageSize ?? 10;

function normalizeListStatus(row: CreditDebitNoteHeader): string {
  const raw = (row as { status?: string }).status;
  return String(raw ?? '')
    .trim()
    .toUpperCase();
}

function statusBadgeClass(statusNorm: string): string {
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED) {
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  }
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED_CANCELLED) {
    return 'bg-red-50 text-red-800 border-red-200';
  }
  if (statusNorm === CreditDebitNoteListStatus.DRAFT) {
    return 'bg-amber-50 text-amber-900 border-amber-200';
  }
  return 'bg-slate-50 text-slate-800 border-slate-200';
}

function statusDisplayLabel(statusNorm: string): string {
  if (!statusNorm) return '—';
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED) return 'E-Invoiced';
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED_CANCELLED) return 'E-Invoiced Cancelled';
  if (statusNorm === CreditDebitNoteListStatus.DRAFT) return 'Draft';
  return statusNorm.replace(/_/g, ' ');
}

function getNoteNumber(row: CreditDebitNoteHeader): string {
  return (row as { noteNumber?: string }).noteNumber ?? row.creditDebitNoteNo ?? `#${row.id}`;
}

function getReferenceInvoiceNo(row: CreditDebitNoteHeader): string {
  return (row as { referenceInvoiceNo?: string }).referenceInvoiceNo ?? row.invoiceNo ?? '—';
}

function getNoteDate(row: CreditDebitNoteHeader): string | undefined {
  return (row as { noteDate?: string }).noteDate ?? row.creditDate;
}

function getInvoiceDate(row: CreditDebitNoteHeader): string | undefined {
  return row.invoiceDate ?? (row as { referenceInvoiceDate?: string }).referenceInvoiceDate;
}

export function CreditDebitNoteListingPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [notes, setNotes] = useState<CreditDebitNoteHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(CREDIT_DEBIT_NOTE_LIST_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<CreditDebitNoteSearchFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [noteTypeFilter, setNoteTypeFilter] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const loadNotes = useCallback(async () => {
    if (selectedBranch == null) {
      setNotes([]);
      setLoading(false);
      setTotalPages(1);
      return;
    }
    try {
      setLoading(true);
      const trimmedSearch = debouncedSearchQuery.trim();
      const requestFilters: CreditDebitNoteSearchFilters = {
        ...filters,
        branchId: selectedBranch,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      };
      const response = await creditDebitNoteApiService.getCreditDebitNotes(currentPage, pageSize, requestFilters);

      if (response && response.success) {
        const noteData = response.data || [];
        setNotes(Array.isArray(noteData) ? noteData : []);
        const apiTotalPages = Number(response.pagination?.totalPages);
        const apiCurrentPage = Number(response.pagination?.currentPage);
        setTotalPages(!Number.isNaN(apiTotalPages) && apiTotalPages > 0 ? apiTotalPages : 1);
        if (!Number.isNaN(apiCurrentPage) && apiCurrentPage > 0 && apiCurrentPage !== currentPage) {
          setCurrentPage(apiCurrentPage);
        }
      } else {
        setNotes([]);
        setTotalPages(1);
      }
    } catch {
      toast.error('Failed to load credit/debit notes');
      setNotes([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters, selectedBranch, debouncedSearchQuery]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleStatusFilterChange = (status: string) => {
    setCurrentPage(1);
    setStatusFilter(status);
    setFilters((prev) => ({
      ...prev,
      status: status && status !== 'all' ? status : undefined,
    }));
  };

  const handleNoteTypeFilterChange = (noteType: string) => {
    setCurrentPage(1);
    setNoteTypeFilter(noteType);
    setFilters((prev) => ({
      ...prev,
      noteType: noteType && noteType !== 'all' ? (noteType as NoteType) : undefined,
    }));
  };

  const handleDateFilterChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleView = (id: number) => navigate(`/invoices/credit-debit-notes/${id}/view`);
  const handleEdit = (id: number) => navigate(`/invoices/credit-debit-notes/${id}/edit`);
  const handleNew = () => navigate('/invoices/credit-debit-notes/new');
  const handleNewManual = () => navigate('/invoices/credit-debit-notes/manual/new');

  const exportToCSV = () => {
    const headers = [
      'Credit/Debit No',
      'Type',
      'Status',
      'Invoice No',
      'Invoice Date',
      'Note Date',
      'Amount',
    ];
    const csvData = notes.map((row) => {
      const noteNumber = getNoteNumber(row);
      const referenceInvoiceNo = getReferenceInvoiceNo(row);
      const invoiceDate = getInvoiceDate(row);
      const noteDate = getNoteDate(row);
      const typeLabel =
        row.noteType === 'DEBIT_NOTE' || String(row.noteType).toUpperCase() === 'DEBIT' ? 'Debit Note' : 'Credit Note';
      return [
        noteNumber,
        typeLabel,
        normalizeListStatus(row) || '',
        referenceInvoiceNo,
        invoiceDate ? formatDate(invoiceDate) : '',
        noteDate ? formatDate(noteDate) : '',
        row.grandTotal != null ? String(row.grandTotal) : '',
      ];
    });
    const csvContent = [headers, ...csvData]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit_debit_notes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Credit / Debit Notes</CardTitle>
                  <CardDescription className="text-primary-100">
                    Manage credit and debit notes against invoices
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleNew}
                  className="bg-white text-primary-700 hover:bg-primary-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Note
                </Button>
                <Button
                  onClick={handleNewManual}
                  variant="outline"
                  className="bg-white/90 text-primary-800 border-white hover:bg-white shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New manual note
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="cdn-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="cdn-search"
                    placeholder="Search by note no, invoice no..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9 border-primary-200 focus-visible:ring-primary-400"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-note-type">Note Type</Label>
                <Select value={noteTypeFilter || 'all'} onValueChange={handleNoteTypeFilterChange}>
                  <SelectTrigger id="cdn-note-type" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="CREDIT_NOTE">Credit Note</SelectItem>
                    <SelectItem value="DEBIT_NOTE">Debit Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-status">Status</Label>
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger id="cdn-status" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value={CreditDebitNoteListStatus.DRAFT}>DRAFT</SelectItem>
                    <SelectItem value={CreditDebitNoteListStatus.E_INVOICED}>E_INVOICED</SelectItem>
                    <SelectItem value={CreditDebitNoteListStatus.E_INVOICED_CANCELLED}>E_INVOICED_CANCELLED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-date-from">Note Date From</Label>
                <Input
                  id="cdn-date-from"
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleDateFilterChange('dateFrom', e.target.value)}
                  className="border-primary-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-date-to">Note Date To</Label>
                <Input
                  id="cdn-date-to"
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleDateFilterChange('dateTo', e.target.value)}
                  className="border-primary-200"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={exportToCSV}
                  variant="outline"
                  className="w-full border-primary-200 text-primary-700 hover:bg-primary-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Note list</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {selectedBranch == null ? (
              <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-pink-50 to-pink-100/50 rounded-lg border border-pink-200">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium mb-2">Select a branch</p>
                <p className="text-sm">Choose a branch from the header to view credit/debit notes.</p>
              </div>
            ) : loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                <p className="mt-2 text-gray-600">Loading credit/debit notes...</p>
              </div>
            ) : notes.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead className="font-semibold text-gray-700">Credit/Debit No</TableHead>
                      <TableHead className="font-semibold text-gray-700">Type</TableHead>
                      <TableHead className="font-semibold text-gray-700">Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">Invoice No</TableHead>
                      <TableHead className="font-semibold text-gray-700">Note Date</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Amount</TableHead>
                      <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notes.map((row) => {
                      const noteNumber = getNoteNumber(row);
                      const referenceInvoiceNo = getReferenceInvoiceNo(row);
                      const noteDate = getNoteDate(row);
                      const invoiceDate = getInvoiceDate(row);
                      const statusNorm = normalizeListStatus(row);
                      const isEInvoicedCancelled =
                        statusNorm === CreditDebitNoteListStatus.E_INVOICED_CANCELLED;
                      return (
                        <TableRow key={row.id} className="hover:bg-primary-50/50">
                          <TableCell
                            className={`font-medium ${isEInvoicedCancelled ? 'text-red-600' : 'text-primary-700'}`}
                          >
                            {noteNumber}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                row.noteType === 'CREDIT_NOTE' || String(row.noteType).toUpperCase() === 'CREDIT'
                                  ? 'default'
                                  : 'secondary'
                              }
                            >
                              {row.noteType === 'DEBIT_NOTE' || String(row.noteType).toUpperCase() === 'DEBIT'
                                ? 'Debit Note'
                                : 'Credit Note'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusBadgeClass(statusNorm)}>
                              {statusDisplayLabel(statusNorm)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-gray-900">{referenceInvoiceNo}</div>
                            {invoiceDate && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                <Calendar className="h-3 w-3 shrink-0" />
                                {formatDate(invoiceDate)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4 shrink-0" />
                              {noteDate ? formatDate(noteDate) : '—'}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold text-gray-900">
                            {row.grandTotal != null ? `₹ ${Number(row.grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleView(row.id)}
                                className="h-8 w-8 p-0 hover:bg-primary-50"
                                title="View note"
                              >
                                <Eye className="h-4 w-4 text-primary-600" />
                              </Button>
                              {isDraftDocumentStatus(statusNorm) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(row.id)}
                                  className="h-8 w-8 p-0 hover:bg-emerald-50"
                                  title="Edit note"
                                >
                                  <Edit className="h-4 w-4 text-emerald-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium mb-2">No credit/debit notes found</p>
                <p className="text-sm mb-4">Try adjusting your search criteria or create a note with New Note.</p>
                <Button
                  onClick={handleNew}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Note
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <Card className="border-primary-200 shadow-md">
            <CardContent className="pt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
